/**
 * Runtime flags: admin surface for behaviour that used to require an env change plus a redeploy.
 *
 * Every flag shows its RESOLVED value and the tier that supplied it (database, environment or default), so an
 * operator can tell a deliberate override from an inherited default. A `dangerous` flag (live trading) cannot be
 * switched on without typing its key.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import type { RuntimeFlag } from '../../generated/api/types.gen.js';
import {
  listOrganizationRuntimeFlagsOptions,
  listOrganizationRuntimeFlagsQueryKey,
  listPlatformRuntimeFlagsOptions,
  listPlatformRuntimeFlagsQueryKey,
  setOrganizationRuntimeFlagMutation,
  setPlatformRuntimeFlagMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Button, useToast } from '../../components/ui/index.js';
import { DashboardPage } from '../../components/page/DashboardPage.js';
import { usePolicy } from '../../hooks/usePolicy.js';
import { PAGE_HELP } from '../../help/index.js';
import { RuntimeFlagConfirmModal } from './RuntimeFlagConfirmModal.js';
import { RuntimeFlagsTable } from './RuntimeFlagsTable.js';
import { requiresTypedConfirmation } from './runtime-flag-rules.js';

export function RuntimeFlagsPage() {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const isSuperadmin = usePolicy('RequireSuperadmin');
  const canManageOrgFlags = usePolicy('CanManageOrgRuntimeFlags');
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<RuntimeFlag | null>(null);

  const platformQuery = useQuery({ ...listPlatformRuntimeFlagsOptions(), enabled: isSuperadmin });
  const orgQuery = useQuery({ ...listOrganizationRuntimeFlagsOptions(), enabled: canManageOrgFlags });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: listPlatformRuntimeFlagsQueryKey() }),
      queryClient.invalidateQueries({ queryKey: listOrganizationRuntimeFlagsQueryKey() }),
    ]);

  const setPlatform = useMutation(setPlatformRuntimeFlagMutation());
  const setOrganization = useMutation(setOrganizationRuntimeFlagMutation());

  const save = async (flag: RuntimeFlag, value: boolean) => {
    setPendingKey(flag.key);
    try {
      if (flag.scope === 'platform') await setPlatform.mutateAsync({ path: { key: flag.key }, body: { value } });
      else await setOrganization.mutateAsync({ path: { key: flag.key }, body: { value } });
      notify(`${flag.label} turned ${value ? 'on' : 'off'}`);
    } catch {
      notify(`Could not save ${flag.label}`, 'danger');
    } finally {
      setPendingKey(null);
      await refresh();
    }
  };

  const onToggle = (flag: RuntimeFlag, value: boolean) => {
    if (value && requiresTypedConfirmation(flag)) setConfirming(flag);
    else void save(flag, value);
  };

  return (
    <DashboardPage
      title="Runtime flags"
      subtitle="Change platform and organization behaviour without a redeploy."
      helpContent={PAGE_HELP['runtime-flags']}
      actions={
        <Button variant="secondary" onClick={() => void refresh()}>
          <RefreshCw size={14} aria-hidden="true" />
          Refresh
        </Button>
      }
    >
      <div className="space-y-8">
        {isSuperadmin && (
          <RuntimeFlagsTable
            title="Platform"
            description="Process behaviour, stored at the system settings tier. Applies to every organization."
            flags={platformQuery.data?.data ?? []}
            loading={platformQuery.isPending}
            failed={platformQuery.isError}
            onRetry={() => void platformQuery.refetch()}
            canWrite={isSuperadmin}
            pendingKey={pendingKey}
            onToggle={onToggle}
          />
        )}
        <RuntimeFlagsTable
          title="Organization"
          description="Capability flags for your active organization."
          flags={orgQuery.data?.data ?? []}
          loading={canManageOrgFlags && orgQuery.isPending}
          failed={orgQuery.isError}
          onRetry={() => void orgQuery.refetch()}
          canWrite={canManageOrgFlags}
          pendingKey={pendingKey}
          onToggle={onToggle}
        />
      </div>
      <RuntimeFlagConfirmModal
        flag={confirming}
        onClose={() => setConfirming(null)}
        onConfirm={(flag) => void save(flag, true)}
      />
    </DashboardPage>
  );
}
